import type { Middleware, MiddlewareAPI } from "@reduxjs/toolkit";
import { changeLanguage } from "i18next";
import { setFilters, setLocale, setSelectedLanguage } from "slices/appStateSlice";
import { setTransaction } from "slices/transactionSlice";
import { type AllergenType, type Maybe, TransactionState } from "types";
import { AppLanguage } from "types/mainTypes";
import { MixpanelActionEventsEnum } from "types/mixpanelTypes";
import { getAnalyticsInstance } from "utils/analytics/analyticsUtils";
import { log } from "utils/logger";

const mapLanguage = (language?: string) => {
	if (language !== "cs" && language !== "sk") {
		return {
			language: AppLanguage.SECONDARY,
			locale: "en-GB",
		};
	}
	return {
		language: AppLanguage.PRIMARY,
		locale: "cs-CZ",
	};
};

const handlePreselectedLanguageSwitch = (store: MiddlewareAPI, language: string) => {
	const mappedLanguage = mapLanguage(language);
	const analytics = getAnalyticsInstance();

	store.dispatch(setSelectedLanguage(mappedLanguage.language));
	changeLanguage(mappedLanguage.locale).catch((e) => {
		log(`Could not change locale ${e}`, "warning");
	});
	const currentLocale = store.getState().appState.locale;
	const fromLanguage = currentLocale?.startsWith("cs") ? "cs" : "en";
	const normalizedLanguage = language === "sk" ? "cs" : language;
	analytics.track(MixpanelActionEventsEnum.Tablet_Language_Changed, {
		$from_language: fromLanguage,
		$to_language: normalizedLanguage,
		$language: normalizedLanguage,
		$trigger_source: "user-preference",
	});
	store.dispatch(setLocale(mappedLanguage.locale));
};

const handlePreselectedFilters = (store: MiddlewareAPI, allergens: Maybe<AllergenType[]>) => {
	const analytics = getAnalyticsInstance();

	if (allergens && allergens.length > 0) {
		const currentAllergens = store.getState().appState.filters.allergens || [];
		// We want to combine allergens that were selected wit the ones from preferences
		const filteredAllergens = allergens.filter((a) => !currentAllergens.includes(a));
		const appliedFilters = [...currentAllergens, ...filteredAllergens];
		analytics.track(MixpanelActionEventsEnum.Tablet_Filter_Applied, {
			$filters: appliedFilters,
			$filter_count: appliedFilters.length,
			$trigger_source: "auto-user-preferences",
		});
		store.dispatch(
			setFilters({
				allergens: appliedFilters,
			}),
		);
	}
};

const seenPreferenceOrderIdsByStore = new WeakMap<MiddlewareAPI, Set<string>>();

const getSeenPreferenceOrderIds = (store: MiddlewareAPI) => {
	let seenOrderIds = seenPreferenceOrderIdsByStore.get(store);
	if (!seenOrderIds) {
		seenOrderIds = new Set<string>();
		seenPreferenceOrderIdsByStore.set(store, seenOrderIds);
	}
	return seenOrderIds;
};

export const userPreferencesMiddleware: Middleware = (store) => (next) => (action) => {
	if (!setTransaction.match(action)) {
		return next(action);
	}

	const result = next(action);

	// CLEAR on finish: when the order we are FOLLOWING reaches Finished, drop the customer's allergen
	// filters so they never bleed into the next customer's flow (finishOrder's resetAppState only runs
	// at OrderStep5 dismissal ~10s later, so a new order starting before then would inherit them).
	// Decided on the POST-reducer slot — pickTransaction's result — not the raw frame, so a stale
	// Finished echo that does NOT win the slot cannot wipe an active customer's filters. `kind === "order"`
	// excludes refills (a restock, not a browse). The length guard makes it idempotent across the
	// repeated Finished echoes the subscription emits while the summary shows. System-driven: no Mixpanel.
	const slot = store.getState().transaction.slot;
	if (
		slot?.kind === "order" &&
		slot.transaction.state === TransactionState.Finished &&
		store.getState().appState.filters.allergens.length > 0
	) {
		store.dispatch(setFilters({ allergens: [] }));
	}

	// APPLY on start: preferences are a flow-BEGIN concern, applied ONCE per order id. The transaction slot
	// can be reset at the end of the flow before the subscription stops echoing the last order; if we keyed
	// only on "slot was empty, now holds this order", that stale ACTIVE echo would look like a fresh order and
	// re-add the finished customer's preferences. The per-store seen set survives that between-customer reset,
	// while still allowing the next customer (a new order id) to get their own preferences.
	if (slot?.kind === "order" && slot.transaction.state !== TransactionState.Finished) {
		const seenOrderIds = getSeenPreferenceOrderIds(store);
		if (seenOrderIds.has(slot.transaction.id)) {
			return result;
		}
		seenOrderIds.add(slot.transaction.id);

		const preferences = slot.transaction.user?.preferences;
		if (preferences?.language) {
			handlePreselectedLanguageSwitch(store, preferences.language);
		}
		if (preferences?.allergens) {
			handlePreselectedFilters(store, preferences.allergens);
		}
	}

	return result;
};
